// docs/120 VM3/VM6: when a band-mate leaves town or takes up a quest, the rest of the band is asked, not
// taken. Over whatever screen a player is on, this card asks them (come along / stay), and shows the one
// who called how the answers are coming in (set out now / call it off). The answers are the module's
// (`band_call`, `calls.rs`): leaving town happens on the answer, a quest once the muster ends.
import { useEffect, useRef, useState } from 'react';
import { Badge, Button, ButtonRow } from '../components';
import { useSession } from '../../core/stores/session';
import { useRoute } from '../../core/stores/route';
import { useBandCalls, useNames, useOffers, useVillagePresence, useVillages } from '../../core/subscriptions/hooks';
import { rpc, useToasts } from '../../core/net/reducers';
import { identityHex, tsToMs } from '../../core/net/connection';
import { serverNow } from '../../core/time';
import { playSfx } from '../../core/audio';
import './bandCalls.css';

/** `calls.rs`: kinds, states and answers. */
const CALL_LEAVE = 0, CALL_QUEST = 1;
const CALL_OPEN = 0, CALL_DONE = 1, CALL_FAILED = 2;
const ANSWER_NONE = 0, ANSWER_COME = 1;

type Call = ReturnType<typeof useBandCalls>[number];

export function BandCalls(): React.JSX.Element | null {
  const worldId = useSession((s) => s.worldId);
  const me = useSession((s) => s.identity ?? '');
  const screen = useRoute((s) => s.screen);
  const go = useRoute((s) => s.go);
  const calls = useBandCalls(worldId);
  const presence = useVillagePresence(worldId);
  const offers = useOffers(worldId);
  const villages = useVillages(worldId);
  const names = useNames(worldId);
  const push = useToasts((s) => s.push);
  const [busy, setBusy] = useState<string>();
  const [, tick] = useState(0);
  const nameOf = (id: { toHexString(): string }) => { const hex = identityHex(id as never); return hex === me ? 'You' : names[hex] || 'A band-mate'; };
  const myRow = presence.find((p) => identityHex(p.owner) === me);
  const asked = (c: Call) => c.asks.find((a) => identityHex(a.owner) === me);
  // what is still mine to answer: an open call that asks me (a call to leave a town I am no longer in is past)
  const open = calls.filter((c) => c.state === CALL_OPEN && asked(c)?.answer === ANSWER_NONE && (c.kind !== CALL_LEAVE || myRow?.villageId === c.villageId));
  const waiting = calls.filter((c) => c.state === CALL_OPEN && c.kind === CALL_QUEST && (identityHex(c.caller) === me || asked(c)?.answer === ANSWER_COME));
  const live = open.length > 0 || waiting.length > 0;
  useEffect(() => {
    if (!live) return;
    const t = window.setInterval(() => tick((n) => n + 1), 500);
    return () => window.clearInterval(t);
  }, [live]);
  // a chime when I am asked; how a muster I am in ended
  const seen = useRef(new Map<bigint, number>());
  useEffect(() => {
    for (const c of calls) {
      const was = seen.current.get(c.id);
      seen.current.set(c.id, c.state);
      const mine = identityHex(c.caller) === me, came = asked(c)?.answer === ANSWER_COME;
      if (was === undefined && c.state === CALL_OPEN && asked(c)?.answer === ANSWER_NONE) playSfx('ui.toast');
      if (was !== CALL_OPEN || c.kind !== CALL_QUEST || !(mine || came)) continue;
      const title = offers.find((o) => o.id === c.offerId)?.title ?? 'the quest';
      if (c.state === CALL_FAILED) push(`“${title}” could not be taken up: ${c.note}.`, 'error');
      if (c.state === CALL_DONE) push(mine ? `You set out on “${title}”${c.asks.some((a) => a.answer === ANSWER_COME) ? ' with your band' : ''}.` : `You set out with ${nameOf(c.caller)} on “${title}”.`, 'success');
    }
  }, [calls]);

  if (!worldId || !live || screen === 'title' || screen === 'encounter' || screen === 'spoils') return null;

  const answer = async (c: Call, come: boolean) => {
    setBusy(`${c.id}-${come}`);
    const r = await rpc.answerBandCall(c.id, come);
    setBusy(undefined);
    if (!r.ok) return;
    const town = villages.find((v) => v.id === c.villageId)?.name ?? 'town';
    if (c.kind === CALL_LEAVE && come) { push(`You leave ${town} with ${nameOf(c.caller)}.`, 'info'); go('world'); }
    else if (c.kind === CALL_LEAVE) push(`You stay in ${town}. You lead your own band now.`, 'info');
    else if (!come) push('You stay behind.', 'info');
  };
  const end = async (c: Call, now: boolean) => { setBusy(`${c.id}-end`); await rpc.endBandCall(c.id, now); setBusy(undefined); };
  const secsLeft = (c: Call) => Math.max(0, Math.ceil(((tsToMs(c.deadline) ?? 0) - serverNow()) / 1000));

  return (
    <div className="fk-calls" data-testid="band-calls">
      {open.map((c) => {
        const who = nameOf(c.caller);
        const town = villages.find((v) => v.id === c.villageId)?.name ?? 'town';
        const title = offers.find((o) => o.id === c.offerId)?.title ?? 'a quest';
        const quest = c.kind === CALL_QUEST;
        return (
          <div key={c.id.toString()} className={`fk-calls__card fk-hit ${quest ? 'fk-calls__card--quest' : ''}`} role="alertdialog" aria-label={quest ? `${who} calls the band to a quest` : `${who} is leaving town`}
            data-testid="band-call" data-kind={quest ? 'quest' : 'leave'} data-call={c.id.toString()}>
            <span className="fk-calls__text">
              {quest
                ? <><b>{who}</b> takes up <b>“{title}”</b>{c.partyId !== c.fromParty ? ' at the head of a fresh band' : ''} and calls you to come.</>
                : <><b>{who}</b> leads the band out of <b>{town}</b>.</>}
            </span>
            <ButtonRow className="fk-calls__choices">
              <Button small primary disabled={!!busy} loading={busy === `${c.id}-true`} onClick={() => void answer(c, true)} data-testid="band-call-come">
                {quest ? 'Come along' : `Go with ${who}`}
              </Button>
              <Button small variant="ghost" disabled={!!busy} loading={busy === `${c.id}-false`} onClick={() => void answer(c, false)} data-testid="band-call-stay"
                title={quest ? (c.partyId !== c.fromParty ? 'Stay with your band' : 'Stay behind and go solo') : `Stay in ${town}, and lead your own band`}>
                {quest ? 'Stay behind' : 'Stay in town (go solo)'}
              </Button>
            </ButtonRow>
            {quest && <Badge tone="gold" className="fk-calls__time">{secsLeft(c)}s</Badge>}
          </div>
        );
      })}
      {waiting.map((c) => {
        const mine = identityHex(c.caller) === me;
        const title = offers.find((o) => o.id === c.offerId)?.title ?? 'the quest';
        return (
          <div key={`w${c.id}`} className="fk-calls__card fk-calls__card--wait fk-hit" role="status" data-testid="band-call-wait" data-call={c.id.toString()}>
            <span className="fk-calls__text">
              {mine ? <>Gathering the band for <b>“{title}”</b>:</> : <>Coming with <b>{nameOf(c.caller)}</b> on <b>“{title}”</b>:</>}
              <span className="fk-calls__who">
                {c.asks.map((a) => <span key={identityHex(a.owner)} className={`fk-calls__ask fk-calls__ask--${a.answer === ANSWER_COME ? 'come' : a.answer === ANSWER_NONE ? 'wait' : 'stay'}`}>{nameOf(a.owner)} {a.answer === ANSWER_COME ? '✓' : a.answer === ANSWER_NONE ? '…' : '✗'}</span>)}
              </span>
            </span>
            {mine && (
              <ButtonRow className="fk-calls__choices">
                <Button small primary disabled={!!busy} loading={busy === `${c.id}-end`} onClick={() => void end(c, true)} data-testid="band-call-go">Set out now</Button>
                <Button small variant="ghost" disabled={!!busy} onClick={() => void end(c, false)} data-testid="band-call-cancel">Call it off</Button>
              </ButtonRow>
            )}
            <Badge tone="gold" className="fk-calls__time">{secsLeft(c)}s</Badge>
          </div>
        );
      })}
    </div>
  );
}

/** Is a quest muster open that I called for this offer (the screen waits for it rather than moving on)? */
export function mustering(calls: ReadonlyArray<Call>, me: string, offerId: bigint): boolean {
  return calls.some((c) => c.state === CALL_OPEN && c.kind === CALL_QUEST && c.offerId === offerId && identityHex(c.caller) === me);
}
